import type { OfferChannel } from '@today/types'
import { EmptyState, FieldShell } from '@today/ui-components'
import { Button, Checkbox, Dialog, Spinner } from '@today/ui-primitives'
import type { ReactElement, ReactNode } from 'react'
import type { Translate } from '../offer/offer-types'

/** Which body Step 4 renders. */
export enum OfferSendStepState {
	LOADING = 'LOADING',
	READY = 'READY',
	ERROR = 'ERROR',
}

/**
 * One channel option.
 *
 * @property reason - The catalog key suffix `send.block.<reason>` explaining a disabled option, or null.
 */
export interface OfferSendChannelOption {
	channel: OfferChannel
	enabled: boolean
	reason: string | null
	selected: boolean
}

/**
 * The expiry field.
 *
 * @property value - The chosen date (`yyyy-mm-dd`), or `''` for no expiry.
 * @property min - The first selectable date.
 * @property max - The last selectable date.
 * @property display - The chosen date formatted for the summary, or null for no expiry.
 */
export interface OfferSendExpiryField {
	value: string
	min: string
	max: string
	display: string | null
}

/** A refused send, already worded, with links back to the steps that fix it. */
export interface OfferSendRefusalView {
	message: string
	links: { label: string; href: string }[]
}

/**
 * Props for {@link OfferSendStepView} — offer wizard Step 4 (TO-125): access,
 * channels, expiry, a confirmation summary and the send.
 *
 * @property consentNotice - The recipient's consents or contacts could not be read: show that the send checks them.
 * @property confirmOpen - Whether the confirmation summary is open.
 * @property recipientName - The recipient named in the summary.
 * @property isSending - True while the PATCH / send is in flight; disables Confirm.
 */
export interface OfferSendStepViewProps {
	t: Translate
	state: OfferSendStepState
	channels: OfferSendChannelOption[]
	consentNotice: boolean
	expiry: OfferSendExpiryField
	sendDisabled: boolean
	confirmOpen: boolean
	recipientName: string
	isSending: boolean
	refusal: OfferSendRefusalView | null
	/** A notice shown above Send, e.g. the notification-message channel warning (TO-140). */
	messageWarning?: ReactNode
	onToggleChannel: (channel: OfferChannel) => void
	onExpiryChange: (value: string) => void
	onExpiryClear: () => void
	onSend: () => void
	onConfirm: () => void
	onCancel: () => void
}

const SECTION = 'flex flex-col gap-2 rounded-ds-md border border-ds-border p-4'
const LEGEND = 'px-1 text-ds-base font-semibold text-ds-text'
const DATE_INPUT =
	'h-10 rounded-ds-md border border-ds-border bg-ds-surface px-3 text-ds-base text-ds-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring'

/** The fixed access mode (PUBLIC only in the MVP). */
function AccessSection({ t }: { t: Translate }): ReactElement {
	return (
		<fieldset className={SECTION}>
			<legend className={LEGEND}>{t('send.access.title')}</legend>
			<p className="text-ds-sm text-ds-text">{t('send.access.public')}</p>
		</fieldset>
	)
}

/** The enabled channels; a disabled one says why. */
function ChannelsSection({ props }: { props: OfferSendStepViewProps }): ReactElement {
	const { t } = props
	return (
		<fieldset className={SECTION}>
			<legend className={LEGEND}>{t('send.channels.title')}</legend>
			{props.channels.length === 0 ? <p className="text-ds-sm text-ds-text-muted">{t('send.channels.none')}</p> : null}
			{props.channels.map((option) => (
				<Checkbox
					key={option.channel}
					isSelected={option.selected}
					isDisabled={!option.enabled}
					onChange={() => props.onToggleChannel(option.channel)}
				>
					<span className="text-ds-sm text-ds-text">{t(`send.channels.${option.channel}`)}</span>
					{option.reason ? (
						<span className="ml-2 text-ds-xs text-ds-text-muted">{t(`send.block.${option.reason}`)}</span>
					) : null}
				</Checkbox>
			))}
			{props.consentNotice ? <p className="text-ds-xs text-ds-text-muted">{t('send.channels.checkOnSend')}</p> : null}
		</fieldset>
	)
}

/** The expiry date, or no expiry. */
function ExpirySection({ props }: { props: OfferSendStepViewProps }): ReactElement {
	const { t, expiry } = props
	return (
		<fieldset className={SECTION}>
			<legend className={LEGEND}>{t('send.expiry.title')}</legend>
			<FieldShell label={t('send.expiry.label')}>
				<input
					type="date"
					className={DATE_INPUT}
					value={expiry.value}
					min={expiry.min}
					max={expiry.max}
					onChange={(event) => props.onExpiryChange(event.target.value)}
				/>
			</FieldShell>
			{expiry.value === '' ? (
				<p className="text-ds-sm text-ds-text-muted">{t('send.expiry.none')}</p>
			) : (
				<div>
					<Button size="sm" variant="muted" onPress={props.onExpiryClear}>
						{t('send.expiry.clear')}
					</Button>
				</div>
			)}
		</fieldset>
	)
}

/** The confirmation summary. */
function ConfirmDialog({ props }: { props: OfferSendStepViewProps }): ReactElement | null {
	const { t } = props
	if (!props.confirmOpen) return null
	const chosen = props.channels.filter((c) => c.selected).map((c) => t(`send.channels.${c.channel}`))
	const rows: [string, string][] = [
		[t('send.confirm.recipient'), props.recipientName],
		[t('send.confirm.access'), t('send.access.public')],
		[t('send.confirm.channels'), chosen.join(', ')],
		[t('send.confirm.expiry'), props.expiry.display ?? t('send.expiry.none')],
	]
	return (
		<Dialog isOpen onOpenChange={(open) => (open ? undefined : props.onCancel())} title={t('send.confirm.title')}>
			<dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-ds-sm">
				{rows.map(([term, value]) => (
					<div key={term} className="contents">
						<dt className="text-ds-text-muted">{term}</dt>
						<dd className="text-ds-text">{value}</dd>
					</div>
				))}
			</dl>
			<div className="mt-4 flex justify-end gap-2">
				<Button variant="secondary" onPress={props.onCancel}>
					{t('send.confirm.cancel')}
				</Button>
				<Button isDisabled={props.isSending} onPress={props.onConfirm}>
					{t('send.confirm.confirm')}
				</Button>
			</div>
		</Dialog>
	)
}

/** A refused send: its message and the links that fix it (announced). */
function Refusal({ refusal }: { refusal: OfferSendRefusalView | null }): ReactElement | null {
	if (!refusal) return null
	return (
		<div role="alert" className="flex flex-col gap-1 text-ds-sm text-ds-danger">
			<p>{refusal.message}</p>
			{refusal.links.length > 0 ? (
				<ul className="flex flex-wrap gap-3">
					{refusal.links.map((link) => (
						<li key={link.href}>
							<a className="underline" href={link.href}>
								{link.label}
							</a>
						</li>
					))}
				</ul>
			) : null}
		</div>
	)
}

/** Offer wizard Step 4 — access, channels, expiry, confirm and send (organism, TO-125). */
export function OfferSendStepView(props: OfferSendStepViewProps): ReactElement {
	const { t } = props
	if (props.state === OfferSendStepState.LOADING) {
		return <Spinner label={t('send.loading')} />
	}
	if (props.state === OfferSendStepState.ERROR) return <EmptyState title={t('send.loadError')} />
	return (
		<div className="flex flex-col gap-4">
			<h2 className="text-ds-lg font-semibold text-ds-text">{t('send.title')}</h2>
			<AccessSection t={t} />
			<ChannelsSection props={props} />
			<ExpirySection props={props} />
			<Refusal refusal={props.refusal} />
			{props.messageWarning ?? null}
			<div>
				<Button isDisabled={props.sendDisabled} onPress={props.onSend}>
					{t('send.submit')}
				</Button>
			</div>
			<ConfirmDialog props={props} />
		</div>
	)
}
